import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { clinicalApi, CodeOption, PatientDiagnosis } from '../../services/api';
import { ClinicalChip, SourceChip, statusColor } from './clinicalBadges';

type Props = {
  spruceEntityId?: string;
  showOngoing?: boolean; // when true, also renders the "Ongoing Medical Problems" column
};

const DX_EVENT = 'patientDiagnosesChange';
const isResolved = (d: PatientDiagnosis) => (d.status || '').toLowerCase() === 'resolved';

const labelSx = {
  color: 'text.secondary',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.06em',
  fontWeight: 600,
  fontSize: '0.68rem',
  lineHeight: 1.2,
};

const PatientDiagnosisManager: React.FC<Props> = ({ spruceEntityId, showOngoing = false }) => {
  const [list, setList] = useState<PatientDiagnosis[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [options, setOptions] = useState<CodeOption[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [searching, setSearching] = useState(false);
  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null);
  const [menuDx, setMenuDx] = useState<PatientDiagnosis | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    if (!spruceEntityId) return;
    setLoading(true);
    try {
      const res = await clinicalApi.listDiagnoses(spruceEntityId, 'all');
      setList(res.data?.data || []);
    } catch {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [spruceEntityId]);

  useEffect(() => {
    load();
  }, [load]);

  // Stay in sync across every instance (pill + bar) on this patient.
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail || !detail.spruceEntityId || detail.spruceEntityId === spruceEntityId) load();
    };
    window.addEventListener(DX_EVENT, onChange);
    return () => window.removeEventListener(DX_EVENT, onChange);
  }, [load, spruceEntityId]);

  const announce = () => {
    try {
      window.dispatchEvent(new CustomEvent(DX_EVENT, { detail: { spruceEntityId } }));
    } catch {
      /* noop */
    }
  };

  // Debounced ICD-10 typeahead.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = inputValue.trim();
    if (q.length < 2) {
      setOptions([]);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await clinicalApi.searchCodes(q, 'icd10');
        setOptions(res.data?.data || []);
      } catch {
        setOptions([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [inputValue]);

  const { ongoing, ptdx } = useMemo(() => {
    const sorted = [...list].sort(
      (a, b) =>
        (isResolved(a) ? 1 : 0) - (isResolved(b) ? 1 : 0) ||
        (a.description || '').toLowerCase().localeCompare((b.description || '').toLowerCase()),
    );
    return { ongoing: sorted.filter((d) => !isResolved(d)), ptdx: sorted };
  }, [list]);

  const handleAdd = async (opt: CodeOption | string | null) => {
    if (!spruceEntityId || !opt) return;
    const payload =
      typeof opt === 'string'
        ? { description: opt.trim(), icd10_code: null as string | null }
        : { description: opt.description, icd10_code: opt.code };
    if (!payload.description) return;
    try {
      await clinicalApi.addDiagnosis(spruceEntityId, payload);
      setAdding(false);
      setInputValue('');
      setOptions([]);
      await load();
      announce();
    } catch {
      /* keep open on failure */
    }
  };

  const openMenu = (e: React.MouseEvent<HTMLElement>, dx: PatientDiagnosis) => {
    setMenuEl(e.currentTarget);
    setMenuDx(dx);
  };
  const closeMenu = () => {
    setMenuEl(null);
    setMenuDx(null);
  };
  const toggleResolved = async () => {
    if (!menuDx) return;
    const next = isResolved(menuDx) ? 'active' : 'resolved';
    closeMenu();
    try {
      await clinicalApi.updateDiagnosis(menuDx.id, {
        status: next,
        stop_date: next === 'resolved' ? new Date().toISOString().slice(0, 10) : null,
      });
      await load();
      announce();
    } catch {
      /* noop */
    }
  };
  const removeDx = async () => {
    if (!menuDx) return;
    closeMenu();
    try {
      await clinicalApi.deleteDiagnosis(menuDx.id);
      await load();
      announce();
    } catch {
      /* noop */
    }
  };

  if (!spruceEntityId) return null;

  const ptdxColumn = (
    <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={labelSx}>
          Patient Diagnosis (PT DX)
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {loading && <CircularProgress size={12} />}
          <Tooltip title="Add diagnosis (ICD-10)">
            <IconButton size="small" onClick={() => setAdding((v) => !v)} sx={{ p: 0.25 }}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {adding && (
        <Autocomplete<CodeOption, false, false, true>
          freeSolo
          size="small"
          autoHighlight
          openOnFocus
          options={options}
          loading={searching}
          filterOptions={(x) => x}
          inputValue={inputValue}
          onInputChange={(_e, v) => setInputValue(v)}
          getOptionLabel={(o) => (typeof o === 'string' ? o : `${o.description} (${o.code})`)}
          renderOption={(props, o) => (
            <li {...props} key={typeof o === 'string' ? o : o.code}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2">{typeof o === 'string' ? o : o.description}</Typography>
                {typeof o !== 'string' && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    ICD-10 {o.code}
                  </Typography>
                )}
              </Box>
            </li>
          )}
          onChange={(_e, val) => handleAdd(val)}
          noOptionsText={inputValue.trim().length < 2 ? 'Type to search ICD-10…' : 'No matching codes'}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              placeholder="Search diagnosis or ICD-10 code…"
              variant="outlined"
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {searching ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}

      {ptdx.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
          No diagnoses recorded
        </Typography>
      ) : (
        // Aligned rows (matches the medication list): ICD-10 code + description
        // with status badges, source tag, and acuity/onset on a muted line.
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {ptdx.map((d, i) => {
            const resolved = isResolved(d);
            const chronic = (d.status || '').toLowerCase() === 'chronic';
            const acuity = d.acuity && d.acuity.toLowerCase() !== 'unspecified' ? d.acuity : '';
            const onset = d.onset_date
              ? new Date(d.onset_date).toLocaleDateString(undefined, { year: 'numeric', month: 'short' })
              : '';
            const meta = [acuity, onset && `Onset ${onset}`, d.comment].filter(Boolean).join(' · ');
            return (
              <Box
                key={`dx-${d.id}`}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 0.5,
                  py: 0.6,
                  borderTop: i ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
                    {d.icd10_code && (
                      <Box
                        component="span"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.68rem',
                          fontWeight: 600,
                          color: 'text.secondary',
                          bgcolor: 'grey.100',
                          px: 0.5,
                          py: 0.1,
                          borderRadius: 0.5,
                        }}
                      >
                        {d.icd10_code}
                      </Box>
                    )}
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.82rem',
                        lineHeight: 1.3,
                        color: resolved ? 'text.disabled' : 'text.primary',
                        textDecoration: resolved ? 'line-through' : 'none',
                      }}
                    >
                      {d.description}
                    </Typography>
                    {chronic && <ClinicalChip label="Chronic" color={statusColor('chronic')} />}
                    {resolved && <ClinicalChip label="Resolved" color={statusColor('resolved')} />}
                    <SourceChip source={d.source} />
                  </Box>
                  {meta && (
                    <Typography sx={{ display: 'block', color: 'text.secondary', fontSize: '0.74rem', lineHeight: 1.35, mt: 0.1 }}>
                      {meta}
                    </Typography>
                  )}
                </Box>
                <IconButton
                  size="small"
                  aria-label="Edit or remove diagnosis"
                  onClick={(e) => openMenu(e, d)}
                  sx={{ p: 0.25, mt: -0.25, flexShrink: 0 }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );

  const ongoingColumn = (
    <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Typography variant="caption" sx={labelSx}>
        Ongoing Medical Problems
      </Typography>
      {ongoing.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
          None recorded
        </Typography>
      ) : (
        <Box component="ul" sx={{ m: 0, pl: 2.25, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {ongoing.map((d) => (
            <Box component="li" key={`og-${d.id}`} sx={{ color: 'primary.main' }}>
              <Typography component="span" variant="body2" sx={{ color: 'text.primary' }}>
                {d.description}
                {d.acuity && d.acuity.toLowerCase() !== 'unspecified' ? (
                  <Box component="span" sx={{ color: 'text.secondary' }}>
                    {' '}
                    · {d.acuity}
                  </Box>
                ) : null}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );

  return (
    <>
      {showOngoing ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            columnGap: { xs: 2, md: 4 },
            rowGap: 2,
            alignItems: 'start',
          }}
        >
          {ongoingColumn}
          {ptdxColumn}
        </Box>
      ) : (
        ptdxColumn
      )}

      <Menu anchorEl={menuEl} open={Boolean(menuEl)} onClose={closeMenu}>
        <MenuItem onClick={toggleResolved}>{menuDx && isResolved(menuDx) ? 'Mark active' : 'Mark resolved'}</MenuItem>
        <MenuItem onClick={removeDx} sx={{ color: 'error.main' }}>
          Delete
        </MenuItem>
      </Menu>
    </>
  );
};

export default PatientDiagnosisManager;
